// API service for Grassline Snake Game
// Handles communication with the server-side leaderboard API

import { CONFIG, getApiUrl, getApiBaseUrl } from './config.js';

class ApiService {
    constructor() {
        // Use getter function to get base URL dynamically (checks HTTPS at runtime)
        this.getBaseUrl = getApiBaseUrl;
        this.isOnline = navigator.onLine;
        this.config = CONFIG;
        this.setupOnlineListener();
    }
    
    // Get base URL dynamically each time (for runtime HTTPS detection)
    get baseUrl() {
        return this.getBaseUrl();
    }

    setupOnlineListener() {
        window.addEventListener('online', () => {
            this.isOnline = true;
            console.log('🌐 Back online - server API available');
        });

        window.addEventListener('offline', () => {
            this.isOnline = false;
            console.log('📴 Offline - using local storage fallback');
        });
    }

    // Generate a unique player ID for tracking personal high scores
    generatePlayerId() {
        let playerId = localStorage.getItem('grassline_player_id');
        if (!playerId) {
            playerId = 'player_' + Date.now() + '_' + Math.random().toString(36).substr(2, 9);
            localStorage.setItem('grassline_player_id', playerId);
        }
        return playerId;
    }

    // Check if server is available
    async checkServerHealth() {
        try {
            const response = await fetch(getApiUrl(CONFIG.ENDPOINTS.HEALTH), {
                method: 'GET',
                timeout: 5000
            });
            return response.ok;
        } catch (error) {
            console.log('Server health check failed:', error.message);
            return false;
        }
    }

    // Submit score to global leaderboard
    async submitScore(name, score, contactDetails = {}) {
        if (!this.isOnline) {
            console.log('📴 Offline - saving to local storage');
            return this.saveScoreLocally(name, score);
        }

        try {
            const playerId = this.generatePlayerId();
            const response = await fetch(getApiUrl(CONFIG.ENDPOINTS.SCORES), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: name.trim(),
                    score: parseInt(score),
                    playerId: playerId,
                    email: contactDetails.email,
                    emailConsent: contactDetails.emailConsent === true
                })
            });

            const result = await response.json();

            if (result.success) {
                console.log('✅ Score saved to global leaderboard');
                // Also save locally as backup
                this.saveScoreLocally(name, score);
                return { success: true, global: true };
            } else {
                throw new Error(result.error || 'Failed to save score');
            }
        } catch (error) {
            console.error('❌ Failed to save score to server:', error.message);
            // Fallback to local storage
            return this.saveScoreLocally(name, score);
        }
    }

    // Get global leaderboard
    async getLeaderboard(limit = 50) {
        if (!this.isOnline) {
            console.log('📴 Offline - loading local leaderboard');
            return this.getLocalLeaderboard(limit);
        }

        try {
            // Add cache-busting timestamp to ensure fresh data
            const timestamp = new Date().getTime();
            const response = await fetch(getApiUrl(CONFIG.ENDPOINTS.LEADERBOARD) + `?limit=${limit}&_t=${timestamp}`, {
                method: 'GET',
                cache: 'no-cache',
                headers: {
                    'Cache-Control': 'no-cache'
                }
            });
            
            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const result = await response.json();

            if (result.success) {
                console.log(`📊 Loaded global leaderboard (${result.total} scores)`);
                // Cache the global leaderboard locally
                this.cacheLeaderboard(result.leaderboard);
                return result.leaderboard;
            } else {
                throw new Error(result.error || 'Failed to load leaderboard');
            }
        } catch (error) {
            console.error('❌ Failed to load global leaderboard:', error.message);
            // Fallback to local leaderboard
            return this.getLocalLeaderboard(limit);
        }
    }

    // Get personal high score
    async getHighScore() {
        if (!this.isOnline) {
            console.log('📴 Offline - loading local high score');
            return this.getLocalHighScore();
        }

        try {
            const playerId = this.generatePlayerId();
            const response = await fetch(getApiUrl(CONFIG.ENDPOINTS.HIGH_SCORE) + `/${playerId}`, {
                method: 'GET',
                timeout: 5000
            });

            const result = await response.json();

            if (result.success) {
                console.log(`🏆 Personal high score: ${result.highScore}`);
                // Update local high score if server has a higher one
                const localHigh = this.getLocalHighScore();
                if (result.highScore > localHigh) {
                    this.setLocalHighScore(result.highScore);
                }
                return result.highScore;
            } else {
                throw new Error(result.error || 'Failed to load high score');
            }
        } catch (error) {
            console.error('❌ Failed to load personal high score:', error.message);
            // Fallback to local high score
            return this.getLocalHighScore();
        }
    }

    // Get game statistics
    async getGameStats() {
        if (!this.isOnline) {
            return this.getLocalStats();
        }

        try {
            const response = await fetch(getApiUrl(CONFIG.ENDPOINTS.STATS), {
                method: 'GET',
                timeout: 5000
            });

            const result = await response.json();

            if (result.success) {
                console.log('📈 Game stats loaded from server');
                return result.stats;
            } else {
                throw new Error(result.error || 'Failed to load stats');
            }
        } catch (error) {
            console.error('❌ Failed to load game stats:', error.message);
            return this.getLocalStats();
        }
    }

    // LOCAL STORAGE FALLBACK METHODS

    saveScoreLocally(name, score) {
        try {
            const leaderboard = this.getLocalLeaderboard();
            const newEntry = {
                id: Date.now().toString(),
                name: name.trim(),
                score: parseInt(score),
                date: new Date().toISOString(),
                timestamp: Date.now()
            };

            leaderboard.push(newEntry);
            leaderboard.sort((a, b) => b.score - a.score);
            
            // Keep only top 100 scores locally
            const trimmedLeaderboard = leaderboard.slice(0, 100);
            localStorage.setItem('grassline_snake_leaderboard', JSON.stringify(trimmedLeaderboard));

            // Update high score if needed
            const currentHigh = this.getLocalHighScore();
            if (score > currentHigh) {
                this.setLocalHighScore(score);
            }

            console.log('💾 Score saved locally');
            return { success: true, global: false };
        } catch (error) {
            console.error('❌ Failed to save score locally:', error);
            return { success: false, error: error.message };
        }
    }

    getLocalLeaderboard(limit = 50) {
        try {
            const leaderboard = JSON.parse(localStorage.getItem('grassline_snake_leaderboard') || '[]');
            return leaderboard.slice(0, limit);
        } catch (error) {
            console.error('❌ Failed to load local leaderboard:', error);
            return [];
        }
    }

    getLocalHighScore() {
        try {
            return parseInt(localStorage.getItem('grassline_snake_high_score') || '0');
        } catch (error) {
            console.error('❌ Failed to load local high score:', error);
            return 0;
        }
    }

    setLocalHighScore(score) {
        try {
            localStorage.setItem('grassline_snake_high_score', score.toString());
        } catch (error) {
            console.error('❌ Failed to save local high score:', error);
        }
    }

    getLocalStats() {
        try {
            const leaderboard = this.getLocalLeaderboard(1000);
            if (leaderboard.length === 0) {
                return {
                    totalGames: 0,
                    highestScore: 0,
                    averageScore: 0,
                    uniquePlayers: 0,
                    lastGame: null
                };
            }

            const uniquePlayers = new Set(leaderboard.map(entry => entry.name)).size;
            const totalScore = leaderboard.reduce((sum, entry) => sum + entry.score, 0);
            const averageScore = Math.round(totalScore / leaderboard.length);

            return {
                totalGames: leaderboard.length,
                highestScore: leaderboard[0]?.score || 0,
                averageScore: averageScore,
                uniquePlayers: uniquePlayers,
                lastGame: leaderboard[0]?.date || null
            };
        } catch (error) {
            console.error('❌ Failed to calculate local stats:', error);
            return {
                totalGames: 0,
                highestScore: 0,
                averageScore: 0,
                uniquePlayers: 0,
                lastGame: null
            };
        }
    }

    // Cache global leaderboard locally for offline viewing
    cacheLeaderboard(leaderboard) {
        try {
            localStorage.setItem('grassline_snake_cached_leaderboard', JSON.stringify(leaderboard));
            localStorage.setItem('grassline_snake_cache_timestamp', Date.now().toString());
        } catch (error) {
            console.error('❌ Failed to cache leaderboard:', error);
        }
    }

    // Get cached leaderboard if available
    getCachedLeaderboard() {
        try {
            const timestamp = parseInt(localStorage.getItem('grassline_snake_cache_timestamp') || '0');
            const cacheAge = Date.now() - timestamp;
            
            // Use cache if it's less than 1 hour old
            if (cacheAge < 3600000) {
                const cached = localStorage.getItem('grassline_snake_cached_leaderboard');
                if (cached) {
                    return JSON.parse(cached);
                }
            }
        } catch (error) {
            console.error('❌ Failed to load cached leaderboard:', error);
        }
        return null;
    }

    // Check if we have recent cached data
    hasRecentCache() {
        try {
            const timestamp = parseInt(localStorage.getItem('grassline_snake_cache_timestamp') || '0');
            const cacheAge = Date.now() - timestamp;
            return cacheAge < 3600000; // 1 hour
        } catch (error) {
            return false;
        }
    }
}

// Export singleton instance
export const apiService = new ApiService();
